/**
 * <ThemeToggleButton> — sun/moon icon button wired to useThemeDensity.
 *
 * When `theme === "light"`, shows Moon icon ("Switch to dark"); when
 * `theme === "dark"`, shows Sun icon ("Switch to light"). Click flips the
 * theme via setTheme (DOM + localStorage) and also fires `onPrefsChange`
 * when supplied so the change PATCHes /me/preferences. AppShell wires
 * `onPrefsChange` to `patchPrefs` from `usePrefsReconcile`; standalone
 * Storybook usage may omit it.
 */
import type { JSX } from "react";
import { Sun, Moon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Icon } from "../../primitives/Icon";
import { useThemeDensity } from "../../runtime/use-theme-density";
import { IconButton } from "./IconButton";

export interface ThemeToggleButtonProps {
  /**
   * Optional sink for persisted prefs. Wired by AppShell to `patchPrefs`
   * from `usePrefsReconcile` so theme flips also save to /me/preferences.
   * Storybook + non-AppShell consumers can omit it.
   */
  onPrefsChange?: (prefs: { theme?: "light" | "dark" | "system" }) => void;
}

export function ThemeToggleButton({ onPrefsChange }: ThemeToggleButtonProps = {}): JSX.Element {
  const { t } = useTranslation("common");
  const { theme, setTheme } = useThemeDensity();
  const next = theme === "light" ? "dark" : "light";
  const label = t("a11y.topbar_theme_toggle", {
    defaultValue: "Toggle light or dark theme",
  });
  const handleClick = (): void => {
    setTheme(next);
    onPrefsChange?.({ theme: next });
  };
  return (
    <IconButton aria-label={label} onClick={handleClick}>
      {theme === "light" ? (
        <Icon as={Moon} name="moon" size={18} />
      ) : (
        <Icon as={Sun} name="sun" size={18} />
      )}
    </IconButton>
  );
}
